const INITIAL_DELAY_MS = 1500;
const STAGGER_MS = 120;
const FADE_MS = 500;
const HOVER_FADE_MS = 250;
const HOLD_MS = 2000;
const CYCLES = 2;
const PULSE_OPACITY = 0.6;
const ROW_TOLERANCE_PX = 8;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!reducedMotion) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}

function init() {
  const groups = Array.from(document.querySelectorAll('[data-ripple-group]')).map((el) => ({
    el,
    mode: el.getAttribute('data-ripple-group') || 'all',
  }));
  if (groups.length === 0) return;

  const allEntries = [];
  const events = [];

  for (const group of groups) {
    const cards = collectCardsIn(group);
    if (cards.length === 0) continue;

    const entries = cards.map(buildEntry);
    allEntries.push(...entries);

    const forward = sortForward(entries);
    const reverse = forward.slice().reverse();
    const waveDuration = (forward.length - 1) * STAGGER_MS + FADE_MS;
    const cycleDuration = waveDuration + HOLD_MS + waveDuration + HOLD_MS;
    let cycleStart = INITIAL_DELAY_MS;

    for (let c = 0; c < CYCLES; c++) {
      const isLastCycle = c === CYCLES - 1;
      const forwardStart = cycleStart;
      const reverseStart = cycleStart + waveDuration + HOLD_MS;

      forward.forEach((entry, i) => {
        events.push({
          at: forwardStart + i * STAGGER_MS,
          run: () => setLogicalState(entry, 'image2'),
        });
      });
      reverse.forEach((entry, i) => {
        events.push({
          at: reverseStart + i * STAGGER_MS,
          run: () => setLogicalState(entry, 'image1'),
        });
      });

      if (isLastCycle) {
        events.push({
          at: reverseStart + (reverse.length - 1) * STAGGER_MS + FADE_MS,
          run: () => entries.forEach(teardownEntry),
        });
      }

      cycleStart += cycleDuration;
    }
  }

  if (events.length === 0) return;
  events.sort((a, b) => a.at - b.at);

  let rippleTime = 0;
  let lastFrame = performance.now();
  let nextEventIdx = 0;

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) lastFrame = performance.now();
    allEntries.forEach((entry) => {
      if (document.hidden) entry.currentAnimation?.pause();
      else entry.currentAnimation?.play();
    });
  });

  function tick(now) {
    const delta = now - lastFrame;
    lastFrame = now;
    if (!document.hidden) {
      rippleTime += delta;
      while (nextEventIdx < events.length && events[nextEventIdx].at <= rippleTime) {
        events[nextEventIdx].run();
        nextEventIdx++;
      }
    }
    if (nextEventIdx < events.length) {
      requestAnimationFrame(tick);
    }
  }

  requestAnimationFrame((t) => {
    lastFrame = t;
    tick(t);
  });
}

function collectCardsIn(group) {
  const productCards = group.el.querySelectorAll('product-card');
  const result = [];
  const groupRect = group.mode === 'visible' ? group.el.getBoundingClientRect() : null;

  for (const card of productCards) {
    const gallery = card.querySelector('.card-gallery');
    if (!gallery) continue;

    const rect = gallery.getBoundingClientRect();

    if (groupRect) {
      const overlaps =
        rect.right > groupRect.left + 1 &&
        rect.left < groupRect.right - 1 &&
        rect.bottom > groupRect.top + 1 &&
        rect.top < groupRect.bottom - 1;
      if (!overlaps) continue;
    }

    const visibleSlides = gallery.querySelectorAll('slideshow-slide:not([hidden])');
    const secondImg =
      visibleSlides.length >= 2 ? visibleSlides[1].querySelector('img.product-media__image') : null;
    const type = secondImg ? 'swap' : 'pulse';

    result.push({ gallery, secondImg, type, top: rect.top + window.scrollY, left: rect.left });
  }

  return result;
}

function buildEntry({ gallery, secondImg, type, top, left }) {
  const entry = {
    gallery,
    type,
    top,
    left,
    overlay: null,
    logicalState: 'image1',
    isHovered: false,
    currentAnimation: null,
    savedPointerEnter: null,
    savedPointerLeave: null,
    onEnter: null,
    onLeave: null,
  };

  if (type === 'swap') {
    const overlay = secondImg.cloneNode(true);
    overlay.className = 'ripple-overlay';
    overlay.removeAttribute('loading');
    overlay.removeAttribute('fetchpriority');
    overlay.removeAttribute('id');
    overlay.removeAttribute('ref');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('decoding', 'async');
    gallery.appendChild(overlay);
    entry.overlay = overlay;

    entry.savedPointerEnter = gallery.getAttribute('on:pointerenter');
    entry.savedPointerLeave = gallery.getAttribute('on:pointerleave');
    if (entry.savedPointerEnter) gallery.removeAttribute('on:pointerenter');
    if (entry.savedPointerLeave) gallery.removeAttribute('on:pointerleave');

    entry.onEnter = (event) => {
      if (event.pointerType !== 'mouse') return;
      entry.isHovered = true;
      applyVisualState(entry, HOVER_FADE_MS);
    };
    entry.onLeave = (event) => {
      if (event.pointerType !== 'mouse') return;
      entry.isHovered = false;
      applyVisualState(entry, HOVER_FADE_MS);
    };
    gallery.addEventListener('pointerenter', entry.onEnter);
    gallery.addEventListener('pointerleave', entry.onLeave);
  }

  return entry;
}

function setLogicalState(entry, state) {
  entry.logicalState = state;
  applyVisualState(entry, FADE_MS);
}

function applyVisualState(entry, durationMs) {
  let target, keyframes;

  if (entry.type === 'swap') {
    target = entry.overlay;
    const showingImage2Logically = entry.logicalState === 'image2';
    const targetVisualImage2 = entry.isHovered ? !showingImage2Logically : showingImage2Logically;
    const toValue = targetVisualImage2 ? 1 : 0;
    const fromValue = parseFloat(getComputedStyle(entry.overlay).opacity) || 0;
    if (Math.abs(fromValue - toValue) < 0.01) return;
    keyframes = [{ opacity: fromValue }, { opacity: toValue }];
  } else {
    target = entry.gallery;
    keyframes = [
      { opacity: 1, offset: 0 },
      { opacity: PULSE_OPACITY, offset: 0.5 },
      { opacity: 1, offset: 1 },
    ];
  }

  entry.currentAnimation?.cancel();
  const anim = target.animate(keyframes, {
    duration: durationMs,
    fill: 'forwards',
    easing: 'ease-in-out',
  });
  entry.currentAnimation = anim;
  if (document.hidden) anim.pause();
  anim.finished
    .finally(() => {
      if (entry.currentAnimation === anim) entry.currentAnimation = null;
    })
    .catch(() => {});
}

function teardownEntry(entry) {
  if (entry.type === 'swap') {
    if (entry.isHovered) {
      const productCard = entry.gallery.closest('product-card');
      productCard?.refs?.slideshow?.next(undefined, { animate: false });
    }
    entry.currentAnimation?.cancel();
    entry.gallery.removeEventListener('pointerenter', entry.onEnter);
    entry.gallery.removeEventListener('pointerleave', entry.onLeave);
    if (entry.savedPointerEnter)
      entry.gallery.setAttribute('on:pointerenter', entry.savedPointerEnter);
    if (entry.savedPointerLeave)
      entry.gallery.setAttribute('on:pointerleave', entry.savedPointerLeave);
    entry.overlay.remove();
  } else {
    entry.currentAnimation?.cancel();
  }
}

function sortForward(entries) {
  const sortedByTop = entries.slice().sort((a, b) => a.top - b.top);
  const rows = [];
  let currentRow = [];
  let rowTop = null;

  for (const entry of sortedByTop) {
    if (rowTop === null || Math.abs(entry.top - rowTop) <= ROW_TOLERANCE_PX) {
      currentRow.push(entry);
      if (rowTop === null) rowTop = entry.top;
    } else {
      rows.push(currentRow);
      currentRow = [entry];
      rowTop = entry.top;
    }
  }
  if (currentRow.length) rows.push(currentRow);

  return rows.flatMap((row) => row.slice().sort((a, b) => a.left - b.left));
}
